import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  IconHeartHandshake,
  IconBolt,
  IconEye,
  IconSend,
  IconStar,
  IconStarFilled,
  IconChevronDown,
  IconMoodEmpty,
  IconBriefcase,
  IconBriefcase2,
  IconMapPin,
  IconClock,
  IconCalendar,
  IconCircleCheck,
  IconShare3,
  IconX,
  IconCopy,
  IconBrandTwitter,
  IconBrandFacebook,
  IconBrandWhatsapp,
} from "@tabler/icons-react";
import classes from "./matchesRedesign.module.css";
import VerificationGate from "../../../components/UI/Talent/VerificationGate/VerificationGate";
import { UserContext } from "../../../context";
import { currencyFormat } from "../../../utils/functions";
import { errorHandler, toastSuccess } from "../../../utils";
import {
  talentGetMatchedJobs,
  talentGetJobInteraction,
  talentJobInteraction,
  talentRemoveJobInteraction,
} from "../../../services/talentCalls";

const TABS = [
  { id: "active", label: "Active", icon: IconBolt },
  { id: "viewed", label: "Viewed", icon: IconEye },
  { id: "applied", label: "Applied", icon: IconSend },
  { id: "starred", label: "Starred", icon: IconStar },
];

const PAGE_SIZE = 10;

const gaugeColor = (score) => {
  if (score >= 80) return "#059669";
  if (score >= 60) return "#1d4ed8";
  return "#d97706";
};

const MatchGauge = ({ score }) => {
  const r = 15;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  const color = gaugeColor(score);
  return (
    <svg className={classes.mcGauge} viewBox="0 0 36 36" width="40" height="40">
      <circle cx="18" cy="18" r={r} fill="none" stroke="#e2e8f4" strokeWidth="4" />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={c.toFixed(2)}
        strokeDashoffset={off.toFixed(2)}
        transform="rotate(-90 18 18)"
      />
      <text x="18" y="20.5" textAnchor="middle" fontSize="10" fontWeight="700" fill={color} fontFamily="Inter, sans-serif">
        {score}
      </text>
    </svg>
  );
};

const Matches = () => {
  const navigate = useNavigate();
  const { data } = useContext(UserContext);
  const [tab, setTab] = useState("active");
  const [sort, setSort] = useState("match-desc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [lists, setLists] = useState({ active: [], viewed: [], applied: [], starred: [] });
  const [totals, setTotals] = useState({ active: 0, viewed: 0, applied: 0, starred: 0 });

  const [selectedJob, setSelectedJob] = useState(null);
  const [shareJob, setShareJob] = useState(null);

  const fetchTab = async (tabId, pageNum = 1) => {
    setLoading(true);
    try {
      let res, records, total;
      if (tabId === "active") {
        res = await talentGetMatchedJobs(pageNum);
        records = res?.data?.data?.records || [];
        total = res?.data?.data?.totalItems || 0;
      } else {
        const interactionType = tabId === "viewed" ? "view" : tabId === "applied" ? "apply" : "star";
        res = await talentGetJobInteraction(interactionType, pageNum);
        records = res?.data?.data?.data || [];
        total = res?.data?.data?.totalItems || 0;
      }
      setLists((prev) => ({ ...prev, [tabId]: records }));
      setTotals((prev) => ({ ...prev, [tabId]: total }));
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTab("active");
    fetchTab("viewed");
    fetchTab("applied");
    fetchTab("starred");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (tabId) => {
    setTab(tabId);
    setPage(1);
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchTab(tab, p);
  };

  const refreshAllTabs = () => {
    fetchTab("active", tab === "active" ? page : 1);
    fetchTab("viewed", tab === "viewed" ? page : 1);
    fetchTab("applied", tab === "applied" ? page : 1);
    fetchTab("starred", tab === "starred" ? page : 1);
  };

  const starredIds = useMemo(() => new Set(lists.starred.map((j) => j._id)), [lists.starred]);

  const talentSkillSet = useMemo(() => {
    const skills = [...(data?.primarySkills || []), ...(data?.secondarySkills || [])];
    return new Set(skills.map((s) => s.toLowerCase()));
  }, [data]);

  const getSkillMatch = (job) => {
    const required = job?.skillsAndRequirements || [];
    if (!required.length || talentSkillSet.size === 0) return null;
    const overlap = required.filter((s) => talentSkillSet.has(s.toLowerCase())).length;
    return Math.round((overlap / required.length) * 100);
  };

  const handleToggleStar = async (job) => {
    try {
      if (starredIds.has(job._id)) {
        await talentRemoveJobInteraction(job._id, "star");
        toastSuccess("Star removed successfully");
      } else {
        await talentJobInteraction({ jobId: job._id, interactionType: "star" });
        toastSuccess("Job starred successfully");
      }
      refreshAllTabs();
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    }
  };

  const handleOpenDetail = async (job) => {
    setSelectedJob(job);
    if (!job.isViewed) {
      try {
        await talentJobInteraction({ jobId: job._id, interactionType: "view" });
      } catch (_) {}
    }
  };

  const currentList = lists[tab] || [];
  const sortedList = useMemo(() => {
    const list = [...currentList];
    list.sort((a, b) => {
      if (sort === "newest") return new Date(b.createdAt || b.startDate || 0) - new Date(a.createdAt || a.startDate || 0);
      if (sort === "salary-desc") return Number(b.budget ?? 0) - Number(a.budget ?? 0);
      if (sort === "salary-asc") return Number(a.budget ?? 0) - Number(b.budget ?? 0);
      return (getSkillMatch(b) ?? -1) - (getSkillMatch(a) ?? -1);
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentList, sort, talentSkillSet]);

  const totalPages = Math.max(1, Math.ceil((totals[tab] || 0) / PAGE_SIZE));

  const pageContent = (
    <div className={classes.main}>
      <div className={classes.pageHeader}>
        <div className={classes.pageTitleRow}>
          <div>
            <span className={classes.eyebrow}>
              <IconHeartHandshake size={14} /> AI-Powered Matching
            </span>
            <h1 className={classes.pageTitle}>Your Matches.</h1>
            <p className={classes.pageSubtitle}>
              Roles matched to your profile — track what's new, what you've viewed, applied to, and starred.
            </p>
          </div>
        </div>
      </div>

      <div className={classes.statShelf}>
        <div className={`${classes.statCard} ${classes.statBlue}`}>
          <div className={classes.statIcon}>
            <IconBolt size={18} />
          </div>
          <div className={classes.statValue}>{totals.active}</div>
          <div className={classes.statLabel}>Active</div>
        </div>
        <div className={`${classes.statCard} ${classes.statIndigo}`}>
          <div className={classes.statIcon}>
            <IconEye size={18} />
          </div>
          <div className={classes.statValue}>{totals.viewed}</div>
          <div className={classes.statLabel}>Viewed</div>
        </div>
        <div className={`${classes.statCard} ${classes.statGreen}`}>
          <div className={classes.statIcon}>
            <IconSend size={18} />
          </div>
          <div className={classes.statValue}>{totals.applied}</div>
          <div className={classes.statLabel}>Applied</div>
        </div>
        <div className={`${classes.statCard} ${classes.statAmber}`}>
          <div className={classes.statIcon}>
            <IconStar size={18} />
          </div>
          <div className={classes.statValue}>{totals.starred}</div>
          <div className={classes.statLabel}>Starred</div>
        </div>
      </div>

      <div className={classes.tabbar}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${classes.tab} ${tab === t.id ? classes.tabActive : ""}`}
            onClick={() => handleTabChange(t.id)}
          >
            <t.icon size={16} /> {t.label}
            <span className={classes.tabCount}>{totals[t.id]}</span>
          </button>
        ))}
      </div>

      <div className={classes.toolbar}>
        <div className={classes.selectWrap}>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="match-desc">Best Match First</option>
            <option value="newest">Newest First</option>
            <option value="salary-desc">Salary: High to Low</option>
            <option value="salary-asc">Salary: Low to High</option>
          </select>
          <IconChevronDown size={15} />
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.mcList}>
          {loading ? (
            <div className={classes.mcEmpty}>
              <div className={classes.mcEmptyTitle}>Loading…</div>
            </div>
          ) : sortedList.length === 0 ? (
            <div className={classes.mcEmpty}>
              <IconMoodEmpty size={32} />
              <div className={classes.mcEmptyTitle}>No matches here yet</div>
              <div className={classes.mcEmptyHint}>Check back soon — new roles are matched to your profile regularly.</div>
            </div>
          ) : (
            sortedList.map((job) => (
              <div key={job._id} className={classes.mcRow}>
                <div className={classes.mcRowMain}>
                  <div className={classes.mcIcon}>
                    <IconBriefcase size={20} />
                  </div>
                  <div className={classes.mcRowContent} onClick={() => handleOpenDetail(job)}>
                    <div className={classes.mcTitleRow}>
                      <span className={classes.mcTitle}>{job.title}</span>
                      {job.isApplied && (
                        <span className={classes.mcAppliedPill}>
                          <IconCircleCheck size={12} /> Applied
                        </span>
                      )}
                    </div>
                    <div className={classes.mcMeta}>
                      {job.type && (
                        <span>
                          <IconBriefcase2 size={13} /> {job.type}
                        </span>
                      )}
                      {job.workMode && (
                        <>
                          <span className={classes.mcMetaDivider} />
                          <span>
                            <IconMapPin size={13} /> {job.workMode}
                          </span>
                        </>
                      )}
                      {job.duration && (
                        <>
                          <span className={classes.mcMetaDivider} />
                          <span>
                            <IconClock size={13} /> {job.duration}
                          </span>
                        </>
                      )}
                    </div>
                    {job.endDate && (
                      <div className={classes.mcDate}>
                        <IconCalendar size={13} /> Deadline {moment(job.endDate).format("DD-MM-YYYY")}
                      </div>
                    )}
                  </div>
                </div>
                <div className={classes.mcRowSide}>
                  <div className={classes.mcSalary}>&#8358;{currencyFormat(Number(job.budget ?? 0))}</div>
                  <div className={classes.mcSideDivider} />
                  {getSkillMatch(job) !== null && (
                    <div className={classes.mcGaugeWrap}>
                      <MatchGauge score={getSkillMatch(job)} />
                      <span className={classes.mcGaugeLabel}>Match</span>
                    </div>
                  )}
                  <div className={classes.mcActions}>
                    <button
                      className={`${classes.mcIconBtn} ${starredIds.has(job._id) ? classes.mcIconBtnActive : ""}`}
                      title={starredIds.has(job._id) ? "Remove star" : "Star match"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStar(job);
                      }}
                    >
                      {starredIds.has(job._id) ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                    </button>
                    <button
                      className={classes.mcIconBtn}
                      title="Share match"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareJob(job);
                      }}
                    >
                      <IconShare3 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className={classes.mcPagination}>
          <button className={classes.mcPageBtn} disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`${classes.mcPageBtn} ${p === page ? classes.mcPageBtnActive : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}
          <button className={classes.mcPageBtn} disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
            ›
          </button>
        </div>
      )}

      {/* ── Match detail modal ── */}
      {selectedJob && (
        <div className={classes.modalBackdrop} onClick={() => setSelectedJob(null)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={classes.modalHeadIcon}>
                <IconBriefcase size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>{selectedJob.title}</div>
                <div className={classes.modalHeadSub}>
                  {selectedJob.workMode} <span>·</span> Matched {moment(selectedJob.createdAt || selectedJob.startDate).fromNow()}
                </div>
              </div>
              <button className={classes.modalClose} onClick={() => setSelectedJob(null)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.detailStats}>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>{selectedJob.applicantTalentIds?.length ?? 0}</div>
                  <div className={classes.detailStatLbl}>Applicants</div>
                </div>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>
                    {getSkillMatch(selectedJob) !== null ? `${getSkillMatch(selectedJob)}%` : "—"}
                  </div>
                  <div className={classes.detailStatLbl}>Skill Match</div>
                </div>
                <div className={classes.detailStat}>
                  <div className={classes.detailStatVal}>
                    {selectedJob.endDate ? moment(selectedJob.endDate).format("DD MMM") : "—"}
                  </div>
                  <div className={classes.detailStatLbl}>Deadline</div>
                </div>
              </div>

              <div className={classes.detailGrid}>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Salary</div>
                  <div className={classes.detailCellVal}>&#8358;{currencyFormat(Number(selectedJob.budget ?? 0))}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Job Type</div>
                  <div className={classes.detailCellVal}>{selectedJob.type || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Work Mode</div>
                  <div className={classes.detailCellVal}>{selectedJob.workMode || "—"}</div>
                </div>
                <div className={classes.detailCell}>
                  <div className={classes.detailCellLabel}>Relocation</div>
                  <div className={classes.detailCellVal}>
                    {selectedJob.hasNoRelocationPlans === "Yes" ? "Not provided" : "Provided"}
                  </div>
                </div>
              </div>

              {selectedJob.description && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>About the role</div>
                  <p className={classes.detailDesc}>{selectedJob.description}</p>
                </div>
              )}

              {selectedJob.acceptanceCriteria && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>Requirements</div>
                  <ul className={classes.detailReqList}>
                    {selectedJob.acceptanceCriteria
                      .split("\n")
                      .filter(Boolean)
                      .map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                  </ul>
                </div>
              )}

              {selectedJob.skillsAndRequirements?.length > 0 && (
                <div className={classes.detailSection}>
                  <div className={classes.detailSectionTitle}>Skills</div>
                  <div className={classes.detailSkills}>
                    {selectedJob.skillsAndRequirements.map((s, i) => (
                      <span key={i} className={classes.detailSkill}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={classes.modalFoot}>
              <button className={`${classes.modalBtn} ${classes.modalBtnStar}`} onClick={() => handleToggleStar(selectedJob)}>
                {starredIds.has(selectedJob._id) ? <IconStarFilled size={14} /> : <IconStar size={14} />}{" "}
                {starredIds.has(selectedJob._id) ? "Starred" : "Star"}
              </button>
              <button className={`${classes.modalBtn} ${classes.modalBtnGhost}`} onClick={() => setSelectedJob(null)}>
                Close
              </button>
              <button
                className={`${classes.modalBtn} ${classes.modalBtnPrimary}`}
                onClick={() => navigate(`/job/${selectedJob._id}`)}
              >
                <IconSend size={14} /> View &amp; Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share modal ── */}
      {shareJob && (
        <div className={classes.modalBackdrop} onClick={() => setShareJob(null)}>
          <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHead}>
              <div className={`${classes.modalHeadIcon} ${classes.modalHeadIconBlue}`}>
                <IconShare3 size={20} />
              </div>
              <div className={classes.modalHeadInfo}>
                <div className={classes.modalHeadTitle}>Share this match</div>
                <div className={classes.modalHeadSub}>{shareJob.title}</div>
              </div>
              <button className={classes.modalClose} onClick={() => setShareJob(null)} aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <div className={classes.modalBody}>
              <div className={classes.shareSocialGrid}>
                {[
                  {
                    Icon: IconBrandTwitter,
                    bg: "#1d9bf0",
                    label: "Twitter",
                    onClick: () =>
                      window.open(
                        `https://twitter.com/intent/tweet?url=${encodeURIComponent(`${window.location.origin}/job/${shareJob._id}`)}&text=${encodeURIComponent("Check out this job match!")}`,
                        "_blank",
                      ),
                  },
                  {
                    Icon: IconBrandFacebook,
                    bg: "#1877f2",
                    label: "Facebook",
                    onClick: () =>
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                  {
                    Icon: IconBrandWhatsapp,
                    bg: "#25d366",
                    label: "WhatsApp",
                    onClick: () =>
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(`Check out this job match: ${window.location.origin}/job/${shareJob._id}`)}`,
                        "_blank",
                      ),
                  },
                ].map(({ Icon, bg, label, onClick }) => (
                  <button key={label} className={classes.shareSocialBtn} onClick={onClick}>
                    <span className={classes.shareSocialIcon} style={{ background: bg }}>
                      <Icon size={19} />
                    </span>
                    {label}
                  </button>
                ))}
              </div>
              <span className={classes.shareCopyLabel}>Or copy link</span>
              <div className={classes.shareCopyRow}>
                <input type="text" readOnly value={`${window.location.origin}/job/${shareJob._id}`} />
                <button
                  className={`${classes.modalBtn} ${classes.modalBtnPrimary} ${classes.shareCopyBtn}`}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${window.location.origin}/job/${shareJob._id}`);
                      toastSuccess("Job link copied to clipboard!");
                      setShareJob(null);
                    } catch {
                      errorHandler("Failed to copy link", "error");
                    }
                  }}
                >
                  <IconCopy size={14} /> Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Gate only on a positive "unverified". The context profile starts as {}, so
  // `!data.profileVerified` was true on first paint and flashed the gate at
  // verified talents arriving from an email link. If the profile never loads,
  // let the page through and rely on the API's own verification checks.
  if (data?.profileVerified === false) {
    return <VerificationGate>{pageContent}</VerificationGate>;
  }

  return pageContent;
};

export default Matches;
